import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Circle,
  Clock,
  XCircle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import bookingService, {
  Booking,
  BookingFilters,
} from "../../services/bookingService";

type FilterFormState = {
  bookingReference: string;
  bookingId: string;
  hotelBookingRefNumber: string;
  hotelConfirmationNumber: string;
  hotelName: string;
  guestName: string;
  guestEmail: string;
  hotelCity: string;
  hotelCountry: string;
  bookingStatus: string;
  paymentStatus: string;
  checkinDateFrom: string;
  checkinDateTo: string;
  checkoutDateFrom: string;
  checkoutDateTo: string;
  bookingDateFrom: string;
  bookingDateTo: string;
};

const FILTER_DEFAULTS: FilterFormState = {
  bookingReference: "",
  bookingId: "",
  hotelBookingRefNumber: "",
  hotelConfirmationNumber: "",
  hotelName: "",
  guestName: "",
  guestEmail: "",
  hotelCity: "",
  hotelCountry: "",
  bookingStatus: "all",
  paymentStatus: "all",
  checkinDateFrom: "",
  checkinDateTo: "",
  checkoutDateFrom: "",
  checkoutDateTo: "",
  bookingDateFrom: "",
  bookingDateTo: "",
};

type SortField =
  | "booking_reference"
  | "hotel_name"
  | "guest_name"
  | "checkin_date"
  | "hotel_confirmation_number"
  | "booking_status"
  | "total_amount"
  | "created_at";
type SortDirection = "asc" | "desc";

const Bookings: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filtersForm, setFiltersForm] = useState<FilterFormState>(() => ({
    ...FILTER_DEFAULTS,
  }));
  const [appliedFilters, setAppliedFilters] = useState<BookingFilters>({});

  useEffect(() => {
    fetchBookings();
  }, [currentPage, sortField, sortDirection, appliedFilters]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingService.getBookings(
        currentPage,
        itemsPerPage,
        {
          ...appliedFilters,
          sort_by: sortField,
          sort_order: sortDirection,
        }
      );
      setBookings(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total);
    } catch (err) {
      setError("Failed to fetch bookings");
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  const activeFiltersCount = Object.keys(appliedFilters).length;

  const updateFilterForm = <K extends keyof FilterFormState>(
    field: K,
    value: FilterFormState[K]
  ) => {
    setFiltersForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    const nextFilters: BookingFilters = {};

    if (filtersForm.bookingReference)
      nextFilters.booking_reference = filtersForm.bookingReference;
    if (filtersForm.bookingId) nextFilters.booking_id = filtersForm.bookingId;
    if (filtersForm.hotelBookingRefNumber)
      nextFilters.hotel_booking_ref_number = filtersForm.hotelBookingRefNumber;
    if (filtersForm.hotelConfirmationNumber)
      nextFilters.hotel_confirmation_number =
        filtersForm.hotelConfirmationNumber;
    if (filtersForm.hotelName) nextFilters.hotel_name = filtersForm.hotelName;
    if (filtersForm.guestName) nextFilters.guest_name = filtersForm.guestName;
    if (filtersForm.guestEmail)
      nextFilters.guest_email = filtersForm.guestEmail;
    if (filtersForm.hotelCity) nextFilters.hotel_city = filtersForm.hotelCity;
    if (filtersForm.hotelCountry)
      nextFilters.hotel_country = filtersForm.hotelCountry;
    if (filtersForm.bookingStatus && filtersForm.bookingStatus !== "all")
      nextFilters.booking_status = filtersForm.bookingStatus;
    if (filtersForm.paymentStatus && filtersForm.paymentStatus !== "all")
      nextFilters.payment_status = filtersForm.paymentStatus;
    if (filtersForm.checkinDateFrom)
      nextFilters.checkin_date_from = filtersForm.checkinDateFrom;
    if (filtersForm.checkinDateTo)
      nextFilters.checkin_date_to = filtersForm.checkinDateTo;
    if (filtersForm.checkoutDateFrom)
      nextFilters.checkout_date_from = filtersForm.checkoutDateFrom;
    if (filtersForm.checkoutDateTo)
      nextFilters.checkout_date_to = filtersForm.checkoutDateTo;
    if (filtersForm.bookingDateFrom)
      nextFilters.booking_date_from = filtersForm.bookingDateFrom;
    if (filtersForm.bookingDateTo)
      nextFilters.booking_date_to = filtersForm.bookingDateTo;

    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setFiltersForm({ ...FILTER_DEFAULTS });
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewBooking = (booking: Booking) => {
    navigate(`/bookings/${booking.id}`);
  };

  const handleEditBooking = (booking: Booking) => {
    navigate(`/bookings/${booking.id}/edit`);
  };

  const handleDeleteBooking = (booking: Booking) => {
    if (
      window.confirm(
        `Are you sure you want to delete booking ${booking.booking_reference}?`
      )
    ) {
      // TODO: Implement delete functionality
      console.log("Delete booking:", booking.id);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
          className={
            i === currentPage
              ? ""
              : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
          }
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          bookings
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="border-border text-muted-foreground hover:bg-secondary disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {pages}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="border-border text-muted-foreground hover:bg-secondary disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Note: Sorting is now handled server-side, so we use bookings directly

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatIndianCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-muted-foreground opacity-30" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="w-4 h-4 text-primary" />
    ) : (
      <ChevronDown className="w-4 h-4 text-primary" />
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "booked (HCN Pending)":
        return <Circle className="w-4 h-4 text-blue-500" />;
      case "pending (HBR Pending)":
        return <Clock className="w-4 h-4 text-amber-500" />;
      case "cancelled":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "text-green-500 bg-green-100";
      case "booked (HCN Pending)":
        return "text-blue-500 bg-blue-100";
      case "pending (HBR Pending)":
        return "text-amber-500 bg-amber-100";
      case "cancelled":
        return "text-red-500 bg-red-100";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  const capitalizeStatus = (value: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

  const getStatusParts = (status: string) => {
    if (!status) {
      return { primary: "", secondary: null as string | null };
    }

    const match = status.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      return {
        primary: capitalizeStatus(match[1].trim()),
        secondary: match[2].trim(),
      };
    }

    return {
      primary: capitalizeStatus(status.trim()),
      secondary: null,
    };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Bookings Management
          </h1>
          <p className="text-muted-foreground">
            Manage hotel reservations and bookings
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="border border-border bg-white">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by booking reference..."
                className="pl-10"
                value={filtersForm.bookingReference}
                onChange={(e) =>
                  updateFilterForm("bookingReference", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleApplyFilters();
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-border text-muted-foreground hover:bg-secondary"
                onClick={handleApplyFilters}
              >
                Search
              </Button>
              <Button
                variant="outline"
                className={`border-border text-muted-foreground hover:bg-secondary relative ${
                  showFilters ? "bg-secondary/80" : ""
                }`}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary text-white text-xs px-2 py-0.5">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
          {showFilters && (
            <div className="border-t border-border pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Booking ID
                  </Label>
                  <Input
                    value={filtersForm.bookingId}
                    onChange={(e) =>
                      updateFilterForm("bookingId", e.target.value)
                    }
                    className="mt-1"
                    placeholder="e.g. BK-10045"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Guest Name
                  </Label>
                  <Input
                    value={filtersForm.guestName}
                    onChange={(e) =>
                      updateFilterForm("guestName", e.target.value)
                    }
                    className="mt-1"
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Guest Email
                  </Label>
                  <Input
                    value={filtersForm.guestEmail}
                    onChange={(e) =>
                      updateFilterForm("guestEmail", e.target.value)
                    }
                    className="mt-1"
                    placeholder="guest@example.com"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Booking Status
                  </Label>
                  <Select
                    value={filtersForm.bookingStatus}
                    onValueChange={(value) =>
                      updateFilterForm("bookingStatus", value)
                    }
                  >
                    <SelectTrigger className="mt-1 bg-white border-border text-foreground">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="booked (HCN Pending)">
                        Booked (HCN Pending)
                      </SelectItem>
                      <SelectItem value="pending (HBR Pending)">
                        Pending (HBR Pending)
                      </SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                      <SelectItem value="checked_in">Checked In</SelectItem>
                      <SelectItem value="checked_out">Checked Out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Hotel Name
                  </Label>
                  <Input
                    value={filtersForm.hotelName}
                    onChange={(e) =>
                      updateFilterForm("hotelName", e.target.value)
                    }
                    className="mt-1"
                    placeholder="e.g. TRP Resort"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Payment Status
                  </Label>
                  <Select
                    value={filtersForm.paymentStatus}
                    onValueChange={(value) =>
                      updateFilterForm("paymentStatus", value)
                    }
                  >
                    <SelectTrigger className="mt-1 bg-white border-border text-foreground">
                      <SelectValue placeholder="All payments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All payments</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Hotel City
                  </Label>
                  <Input
                    value={filtersForm.hotelCity}
                    onChange={(e) =>
                      updateFilterForm("hotelCity", e.target.value)
                    }
                    className="mt-1"
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Hotel Country
                  </Label>
                  <Input
                    value={filtersForm.hotelCountry}
                    onChange={(e) =>
                      updateFilterForm("hotelCountry", e.target.value)
                    }
                    className="mt-1"
                    placeholder="Country"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Hotel Booking Ref Number
                  </Label>
                  <Input
                    value={filtersForm.hotelBookingRefNumber}
                    onChange={(e) =>
                      updateFilterForm("hotelBookingRefNumber", e.target.value)
                    }
                    className="mt-1"
                    placeholder="HB12345"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Hotel Confirmation Number
                  </Label>
                  <Input
                    value={filtersForm.hotelConfirmationNumber}
                    onChange={(e) =>
                      updateFilterForm(
                        "hotelConfirmationNumber",
                        e.target.value
                      )
                    }
                    className="mt-1"
                    placeholder="CN67890"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Booking Date From
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.bookingDateFrom}
                    onChange={(e) =>
                      updateFilterForm("bookingDateFrom", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Booking Date To
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.bookingDateTo}
                    onChange={(e) =>
                      updateFilterForm("bookingDateTo", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Check-in Date From
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.checkinDateFrom}
                    onChange={(e) =>
                      updateFilterForm("checkinDateFrom", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Check-in Date To
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.checkinDateTo}
                    onChange={(e) =>
                      updateFilterForm("checkinDateTo", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Check-out Date From
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.checkoutDateFrom}
                    onChange={(e) =>
                      updateFilterForm("checkoutDateFrom", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Check-out Date To
                  </Label>
                  <Input
                    type="date"
                    value={filtersForm.checkoutDateTo}
                    onChange={(e) =>
                      updateFilterForm("checkoutDateTo", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  className="border-border text-muted-foreground hover:bg-secondary"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleApplyFilters}
                  className="bg-primary text-white hover:bg-primary/90"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="border border-border bg-white">
        <CardHeader>
          <CardTitle className="text-foreground">Recent Bookings</CardTitle>
          <CardDescription className="text-muted-foreground">
            All hotel bookings and their current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      {getSortIcon("created_at")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("booking_reference")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Booking Ref</span>
                      {getSortIcon("booking_reference")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("hotel_name")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Hotel</span>
                      {getSortIcon("hotel_name")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("guest_name")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Guest</span>
                      {getSortIcon("guest_name")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("checkin_date")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Dates</span>
                      {getSortIcon("checkin_date")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("hotel_confirmation_number")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Hotel Confirmation</span>
                      {getSortIcon("hotel_confirmation_number")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("booking_status")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {getSortIcon("booking_status")}
                    </div>
                  </th>
                  <th
                    className="text-left py-3 px-4 text-muted-foreground font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("total_amount")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount</span>
                      {getSortIcon("total_amount")}
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-8 px-4 text-center text-muted-foreground"
                    >
                      Loading bookings...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-8 px-4 text-center text-destructive"
                    >
                      {error}
                    </td>
                  </tr>
                ) : bookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-8 px-4 text-center text-muted-foreground"
                    >
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => {
                    const checkinDate = new Date(booking.checkin_date);
                    const checkoutDate = new Date(booking.checkout_date);
                    const nights = Math.ceil(
                      (checkoutDate.getTime() - checkinDate.getTime()) /
                        (1000 * 60 * 60 * 24)
                    );
                    const adultsCount =
                      booking.adults ?? booking.adults_count ?? 0;
                    const childrenCount =
                      booking.children ?? booking.children_count ?? 0;
                    const totalGuests = adultsCount + childrenCount;

                    return (
                      <tr
                        key={booking.id}
                        className="border-b border-border hover:bg-secondary/80"
                      >
                        <td className="py-4 px-4">
                          <span className="text-foreground">
                            {formatDate(booking.created_at)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <span className="text-foreground font-medium">
                              {booking.booking_reference}
                            </span>
                            <p className="text-muted-foreground text-xs mt-1">
                              Hotel Ref:{" "}
                              {booking.hotel_booking_ref_number &&
                              booking.hotel_booking_ref_number.trim().length > 0
                                ? booking.hotel_booking_ref_number
                                : "Not provided"}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-foreground font-medium">
                              {booking.hotel_name}
                            </p>
                            <p className="text-muted-foreground text-sm">
                              {totalGuests} guests
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-foreground">
                              {booking.guest_name}
                            </p>
                            <p className="text-muted-foreground text-sm">
                              {booking.guest_email}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-foreground">
                              {formatDate(booking.checkin_date)} -{" "}
                              {formatDate(booking.checkout_date)}
                            </p>
                            <p className="text-muted-foreground text-sm">
                              {nights} nights
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-muted-foreground text-sm">
                            {booking.hotel_confirmation_number &&
                            booking.hotel_confirmation_number.trim().length > 0
                              ? booking.hotel_confirmation_number
                              : "Not provided"}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(booking.booking_status)}
                            {(() => {
                              const { primary, secondary } = getStatusParts(
                                booking.booking_status
                              );
                              return (
                                <span
                                  className={`inline-flex flex-col items-center gap-0.5 text-center rounded-full px-3 py-1 min-w-[120px] ${getStatusColor(
                                    booking.booking_status
                                  )}`}
                                >
                                  <span className="text-xs font-medium leading-tight">
                                    {primary}
                                  </span>
                                  {secondary && (
                                    <span className="text-[8px] leading-tight opacity-80">
                                      {secondary}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-foreground font-medium">
                            ₹{formatIndianCurrency(booking.total_amount)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-primary/40 text-primary hover:bg-primary/10 hover:text-primary transition-colors"
                              onClick={() => handleViewBooking(booking)}
                              title="View booking details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={`${
                                booking.booking_status === "cancelled"
                                  ? "border-border text-muted-foreground bg-muted cursor-not-allowed"
                                  : "border-emerald-400 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                              }`}
                              onClick={() =>
                                booking.booking_status !== "cancelled" &&
                                handleEditBooking(booking)
                              }
                              title={
                                booking.booking_status === "cancelled"
                                  ? "Cannot edit cancelled bookings"
                                  : "Edit booking"
                              }
                              disabled={booking.booking_status === "cancelled"}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-400 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 transition-colors"
                              onClick={() => handleDeleteBooking(booking)}
                              title="Delete booking"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </CardContent>
      </Card>
    </div>
  );
};

export default Bookings;
